import {
    ExpressCheckoutElement,
    PaymentElement,
} from '@stripe/react-stripe-js'
import React, { useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js';
import Button from '../../Button';
import { validateEmail } from '../../../utils/validation';
import { CHECKOUT_CONTEXT_LOYALTY } from '../../../actions/creators/checkout';
import FieldValidationMessage from '../../forms/FieldValidationMessage';
import { centsToCurrencyString } from '../../../utils/numbers';

interface Props {
    amount: number,
    clientSecret: string,
    context: string,
    returnUrl?: string,
    transactionId?: string,
    user: any, // @todo Define user info model
    createPaymentIntent: Function,
    onError: Function,
    waitForPaymentResult: Function,
}

export default function StripeForm(props: Props) {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [email, setEmail] = useState(props.user ? props.user.email : '');
    const [name, setName] = useState(props.user ? [props.user.first_name, props.user.last_name || props.user.f_last_name, props.user.mothers_last_name || props.user.m_last_name].join(' ') : '');
    const [cardValid, setCardValid] = useState<boolean>(false);

    const handlePaymentConfirmation = async (paymentIntentParams?: { name: string, email: string }) => {
        if (!stripe || !elements) return false;
        if (processing) return;

        setProcessing(true);
        setMessage('');

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setProcessing(false);
            props.onError(submitError.message || 'Ocurrió un error. Por favor, verifica los datos ingresados.');
            return false;
        }
        
        const clientSecret = props.clientSecret;
        const transactionId = props.transactionId;
        const confirmParams = {
            elements,
            clientSecret,
            confirmParams: {
                return_url: props.returnUrl,
                payment_method_data: {
                    billing_details: {}
                },
            },
            redirect: 'if_required'
        };

        if (paymentIntentParams && ('name' in paymentIntentParams) && ('email' in paymentIntentParams)) {
            confirmParams.confirmParams.payment_method_data = {
                billing_details: {
                    name: paymentIntentParams.name,
                    email: paymentIntentParams.email,
                }
            };
        }

        // @ts-ignore
        const { error } = await stripe.confirmPayment(confirmParams);
        
        setProcessing(false);

        if (error) {
            props.onError('Ocurrió un error al confirmar el pago.');
            return false;
        } else {
            props.waitForPaymentResult(transactionId);
            return true;
        }
    };

    const onCardFormSubmit = async ev => {
        ev.preventDefault();

        if (!stripe || !elements) return;

        if (!email || !validateEmail(email)) {
            setMessage('Por favor, ingresa tu email.');
            return;
        }

        if (!name) {
            setMessage('Por favor, ingresa tu nombre.');
            return;
        }

        if (!cardValid) {
            setMessage('Por favor, completa los datos de tu tarjeta.');
            return;
        }

        await handlePaymentConfirmation({ name, email });
    };

    async function onWalletSubmit() {
        if (processing) return;
        await handlePaymentConfirmation();
    }

    const isEmailDisabled = props.context !== CHECKOUT_CONTEXT_LOYALTY ? false : (email ? true : false);
    const emailValid = email.length > 0 && validateEmail(email);
    const nameValid = name.length > 0;
    
    return <div className="container pad-y">
        <div className='mb-5'>
            <ExpressCheckoutElement
                onConfirm={onWalletSubmit}
                onClick={({ resolve }) => {
                    resolve({
                        emailRequired: true,
                    })
                }}
                options={{
                    paymentMethods: {
                        googlePay: 'always',
                        applePay: 'always',
                        link: 'auto',
                    },
                    layout: {
                        overflow: 'never'
                    }
                }}
            />
        </div>
        <form id="payment-form" className='was-validated' onSubmit={onCardFormSubmit}>
            <div className='form-group form-group--label-helper not-empty'>
                <label>Email</label>
                <input type='email' className={`form-control form-control-${emailValid ? '' : 'invalid'}`} placeholder='Email' onChange={e => setEmail(e.target.value)} value={email} disabled={isEmailDisabled}/>
                {!emailValid && <FieldValidationMessage message='Por favor, ingresa un email válido.' />}
            </div>
            <div className='form-group form-group--label-helper not-empty'>
                <label>Nombre y apellido</label>
                <input type='text' className={`form-control form-control-${nameValid ? '' : 'invalid'}`} placeholder='Nombre y apellido' onChange={e => setName(e.target.value)} value={name}/>
                {!nameValid && <FieldValidationMessage message='Por favor, ingresa tu nombre y apellido.' />}
            </div>
            <PaymentElement options={{
                layout: 'tabs',
                fields: {
                    billingDetails: {
                        name: 'never',
                        email: 'never',
                    },
                }
            }} onChange={e => setCardValid(e.complete)} />
            <div className='pt-5'>
                <Button primary block submit disabled={!(cardValid && emailValid && nameValid)}>
                    Pagar ({centsToCurrencyString(props.amount)})
                </Button>
            </div>
            {message && <div id="payment-message" className="pt-3">{message}</div>}
        </form>
    </div>;
}